/**
 * Novedades de la plataforma: material de consulta para el equipo de soporte.
 *
 * No es un changelog para el cliente final. Quien lee esto esta atendiendo a
 * un usuario y necesita saber, en segundos, que cambio, si le aplica a quien
 * tiene al telefono, donde esta en pantalla, si el cliente debe hacer algo y
 * como responder las dudas tipicas.
 *
 * Es contenido estatico: publicar una novedad significa agregar un objeto
 * arriba del array y desplegar el front. No hay tabla ni endpoint detras.
 *
 * Reglas de edicion:
 *  - El array va del release mas reciente al mas antiguo; el primero es el
 *    que la pantalla abre por defecto.
 *  - `fecha` en formato YYYY-MM-DD. Es la llave del tab, asi que no se repite.
 *  - `id` de cada cambio: slug estable y unico dentro de su release.
 *  - `tipo` debe existir en pages/Novedades/tipos.js
 *    ("nuevo" | "mejora" | "correccion").
 *  - `perfiles` son nombres legibles y deben coincidir con las llaves de
 *    PROFILES (components/VerticalLayout/SidebarContent.js), mas "Analista",
 *    que existe en el backend pero falta en esa constante.
 *  - `accion` vacio significa que el cambio es automatico y el cliente no
 *    tiene que hacer nada.
 *  - `faq` es el campo que mas le sirve a soporte: la respuesta va redactada
 *    tal cual se le puede leer al cliente.
 *  - Redactar en lenguaje de negocio, no en lenguaje de commit.
 */
const NOVEDADES = [
    {
        version: "v1.9",
        fecha: "2026-09-20",
        resumen:
            "Bitacora de los WhatsApp enviados y correcciones en zonas, configuraciones y acceso de operadores.",
        cambios: [
            {
                id: "bitacora-wa",
                tipo: "nuevo",
                titulo: "Bitácora de envíos de WhatsApp",
                descripcion:
                    "Nueva pantalla con el detalle de cada mensaje de WhatsApp que envía la plataforma: a quién se envió, por qué motivo (reporte diario, validación de PIN, etc.), la fecha y si el proveedor lo aceptó o falló. Se puede filtrar por rango de fechas y abrir el detalle para ver la respuesta completa del proveedor.",
                perfiles: ["Reseller"],
                donde: "Menú lateral > WA enviados",
                accion: "",
                faq: [
                    {
                        pregunta:
                            "El cliente dice que no le llegó el reporte diario. ¿Cómo lo verifico?",
                        respuesta:
                            "Abrir WA enviados, filtrar por la fecha del reporte y buscar el número del cliente. Si el envío no aparece, el mensaje nunca salió (revisar la configuración de notificaciones de la ruta). Si aparece como Falló, el detalle trae la respuesta del proveedor con el motivo.",
                    },
                    {
                        pregunta:
                            "Aparece como Aceptado pero el cliente no lo recibió. ¿Qué le digo?",
                        respuesta:
                            "Aceptado significa que el proveedor recibió el mensaje y lo encoló, no que se entregó. Los fallos posteriores de entrega (número sin WhatsApp, plantilla no aprobada, ventana de 24 horas vencida) no se registran hoy en la bitácora. Confirmar con el cliente que el número tenga WhatsApp activo y que sea el que está cargado en la plataforma.",
                    },
                ],
            },
            {
                id: "color-zonas",
                tipo: "correccion",
                titulo: "El color de las zonas ya se guarda",
                descripcion:
                    "Al dar de alta o editar una zona, el color que se elegía se descartaba al guardar y la zona quedaba sin color. Ya se conserva y se refleja en el catálogo.",
                perfiles: ["Administrador"],
                donde: "Menú lateral > Catálogos > Zonas",
                accion:
                    "Las zonas creadas antes de esta corrección quedaron sin color: hay que editarlas y volver a elegirlo.",
                faq: [
                    {
                        pregunta: "¿Se perdió alguna zona o algún dato?",
                        respuesta:
                            "No. Solo el color no se guardaba; el nombre, la ruta y las geocercas asociadas siempre quedaron correctos.",
                    },
                ],
            },
            {
                id: "login-operador",
                tipo: "correccion",
                titulo: "El operador entra directo a su tablero",
                descripcion:
                    "Los usuarios con perfil Operador aterrizaban en la pantalla de su perfil al iniciar sesión, lo que hacía parecer que no tenían acceso al sistema. Ahora abren directamente el tablero con las salidas de su día.",
                perfiles: ["Operador"],
                donde: "Al iniciar sesión",
                accion: "",
                faq: [
                    {
                        pregunta:
                            "Un operador dice que sigue viendo su perfil al entrar. ¿Qué reviso?",
                        respuesta:
                            "Pedirle que cierre sesión y vuelva a entrar, y que recargue la página con Ctrl+F5 para que tome la versión nueva de la plataforma.",
                    },
                ],
            },
            {
                id: "configuraciones-duplicadas",
                tipo: "correccion",
                titulo: "Configuraciones de ruta duplicadas",
                descripcion:
                    "Una configuración de ruta tenía el nombre mal escrito y se guardaba como un registro nuevo en lugar de actualizar el existente, dejando parámetros duplicados en la ruta.",
                perfiles: ["Administrador", "Reseller"],
                donde: "Menú lateral > Catálogos > Rutas > Configuración de la ruta",
                accion: "",
                faq: [
                    {
                        pregunta:
                            "El cliente vio parámetros repetidos en su ruta. ¿Se corrigen solos?",
                        respuesta:
                            "Los registros duplicados que ya existían no desaparecen solos. Si el cliente los reporta, escalar con el equipo técnico indicando la ruta para limpiarlos.",
                    },
                ],
            },
        ],
    },
    {
        version: "v1.8",
        fecha: "2026-09-19",
        resumen:
            "Boletera: cobro por distancia, catálogo de zonas y tablero del operador.",
        cambios: [
            {
                id: "precios-zona-a-zona",
                tipo: "nuevo",
                titulo: "Precios zona a zona por ramal",
                descripcion:
                    "Cada ramal puede cobrar por distancia con una matriz de precios entre zonas, como alternativa a la tarifa fija. La boletera calcula el precio según la zona de subida y la de bajada.",
                perfiles: ["Administrador"],
                donde: "Menú lateral > Catálogos > Ramales > Precios",
                accion:
                    "Para usar cobro por distancia, el cliente primero debe dar de alta sus zonas, asociarlas a las geocercas del ramal y capturar la matriz de precios. Mientras no lo haga, el ramal sigue cobrando tarifa fija.",
                faq: [
                    {
                        pregunta: "¿Deja de funcionar la tarifa fija?",
                        respuesta:
                            "No. La tarifa fija sigue siendo el modo por defecto; el cobro por distancia se activa por ramal y solo si el cliente lo configura.",
                    },
                    {
                        pregunta:
                            "El operador ve un precio distinto al esperado. ¿Dónde reviso?",
                        respuesta:
                            "En la pantalla de precios del ramal, verificar la celda de la zona de origen contra la de destino. Si alguna geocerca no tiene zona asignada, el cálculo cae a la tarifa fija.",
                    },
                ],
            },
            {
                id: "catalogo-zonas",
                tipo: "nuevo",
                titulo: "Catálogo de zonas",
                descripcion:
                    "Alta de zonas por ruta y su relación con las geocercas del itinerario. Es la base del cobro por distancia de la boletera.",
                perfiles: ["Administrador"],
                donde: "Menú lateral > Catálogos > Zonas",
                accion:
                    "Cada geocerca del ramal debe quedar asociada a una zona para que el cobro por distancia funcione completo.",
                faq: [
                    {
                        pregunta: "¿Una geocerca puede estar en varias zonas?",
                        respuesta:
                            "No. Cada geocerca pertenece a una sola zona; así el sistema puede determinar sin ambigüedad el precio del tramo.",
                    },
                ],
            },
            {
                id: "tablero-operador",
                tipo: "nuevo",
                titulo: "Tablero de salidas del operador",
                descripcion:
                    "El perfil Operador cuenta con un tablero con las salidas de su día, el detalle de cada salida como pantalla completa (no un modal) y el acceso a la boletera para vender boletos.",
                perfiles: ["Operador"],
                donde: "Al iniciar sesión con perfil Operador",
                accion:
                    "El operador necesita un usuario de acceso activo. Se genera desde el catálogo de operadores (ver la novedad del 16 de septiembre).",
                faq: [
                    {
                        pregunta: "El operador no ve ninguna salida en su tablero.",
                        respuesta:
                            "Revisar que las salidas del día estén generadas para su ruta y que la unidad y el operador estén asignados a esa salida. El tablero solo muestra las salidas donde ese operador está asignado.",
                    },
                ],
            },
            {
                id: "imagenes-perfil",
                tipo: "mejora",
                titulo: "Imágenes del perfil de usuario",
                descripcion:
                    "Se ajustó el tamaño de las imágenes en la pantalla de perfil, que se desbordaban y tapaban los datos en pantallas chicas.",
                perfiles: ["Reseller", "Administrador", "Concesionario", "Operador"],
                donde: "Menú de usuario (arriba a la derecha) > Perfil",
                accion: "",
                faq: [],
            },
        ],
    },
    {
        version: "v1.7",
        fecha: "2026-09-16",
        resumen: "Acceso de operadores y estabilidad de la plataforma.",
        cambios: [
            {
                id: "activacion-usuario-operador",
                tipo: "nuevo",
                titulo: "Activación del usuario de acceso desde el catálogo de operadores",
                descripcion:
                    "Desde el catálogo de operadores se puede generar y activar el usuario con el que el operador entra a la plataforma, sin tener que darlo de alta aparte en el catálogo de usuarios.",
                perfiles: ["Administrador"],
                donde: "Menú lateral > Catálogos > Operadores > botón de activar usuario",
                accion:
                    "El cliente debe activar el usuario de cada operador que vaya a usar el tablero o la boletera.",
                faq: [
                    {
                        pregunta: "¿Con qué credenciales entra el operador?",
                        respuesta:
                            "Con el correo que quedó registrado en el operador y la contraseña que se define al activarlo. Si el operador no tiene correo capturado, hay que agregarlo antes de activar.",
                    },
                    {
                        pregunta: "¿Se puede desactivar después?",
                        respuesta:
                            "Sí, desde la misma pantalla. Al desactivarlo el operador deja de poder iniciar sesión, pero su historial de salidas se conserva.",
                    },
                ],
            },
            {
                id: "estabilidad-spa",
                tipo: "correccion",
                titulo: "Cierres inesperados de la plataforma",
                descripcion:
                    "Se corrigieron dos cierres inesperados: uno que ocurría cuando el navegador traducía la página automáticamente (típico en Chrome con el traductor activo) y otro al salir de una pantalla mientras todavía estaba cargando.",
                perfiles: ["Reseller", "Administrador", "Concesionario", "Operador"],
                donde: "Toda la plataforma",
                accion: "",
                faq: [
                    {
                        pregunta:
                            "A un cliente se le sigue cerrando la pantalla. ¿Qué le pido?",
                        respuesta:
                            "Que recargue con Ctrl+F5 para tomar la versión nueva y que desactive la traducción automática del navegador. Si persiste, el error queda registrado en Errores del sistema con la pantalla y el usuario.",
                    },
                ],
            },
        ],
    },
    {
        version: "v1.6",
        fecha: "2026-09-13",
        resumen: "Monitoreo de ruta en tiempo real y bitácora de errores.",
        cambios: [
            {
                id: "monitoreo-ruta-v2",
                tipo: "nuevo",
                titulo: "Monitoreo de ruta v2",
                descripcion:
                    "Tablero con las salidas por concluir y la operación en curso, pensado para dejarlo abierto en una pantalla de control.",
                perfiles: ["Administrador", "Analista"],
                donde: "Menú lateral > Monitoreo",
                accion: "",
                faq: [
                    {
                        pregunta: "¿Cada cuánto se actualiza?",
                        respuesta:
                            "Solo, cada pocos segundos, sin recargar la página. Si el cliente ve datos congelados, pedirle que recargue: probablemente perdió la conexión.",
                    },
                ],
            },
            {
                id: "estatus-salidas-cinta",
                tipo: "mejora",
                titulo: "Rediseño de Estatus Salidas",
                descripcion:
                    "La pantalla de estatus muestra ahora una cinta de unidades por ramal, más fácil de leer de un vistazo que el listado anterior.",
                perfiles: ["Administrador", "Analista"],
                donde: "Menú lateral > Estatus salidas",
                accion: "",
                faq: [],
            },
            {
                id: "bitacora-errores",
                tipo: "nuevo",
                titulo: "Bitácora de errores del sistema",
                descripcion:
                    "Pantalla con los fallos que reportan los navegadores de los usuarios: pantalla donde ocurrió, usuario, perfil y detalle técnico. Sirve para dar seguimiento a las incidencias que reporta el cliente.",
                perfiles: ["Reseller"],
                donde: "Menú lateral > Errores del sistema",
                accion: "",
                faq: [
                    {
                        pregunta: "Un cliente reporta un error. ¿Cómo lo documento?",
                        respuesta:
                            "Buscar el error por fecha y usuario en la bitácora, abrir el detalle y usar el botón Copiar todo para pegar la información completa en el ticket.",
                    },
                ],
            },
        ],
    },
];

export { NOVEDADES };
